/* Gunrange shop — results behaviour for sections/gr-main-results.liquid
   (collection, brand and search pages). Loaded by that section.

   Everything here enhances markup that already works without JavaScript:
   the filter and sort forms are GET forms, chips and page numbers are links,
   Load more is a link to the next page. With this module:
   - a filter or sort change re-renders the results through the Section
     Rendering API (GR.fetchSection) and pushes the new URL; back/forward
     re-render from the URL;
   - chips, "Clear all" and page numbers ([data-gr-nav]) do the same;
   - Load more ([data-gr-load-more]) appends the next page's cards and swaps
     the pager, keeping its live status region so the new count is announced;
   - the long-list find box ([data-gr-find]) narrows a filter's values.

   If a re-render fails, the browser navigates to the same URL instead, so
   the visitor always ends up on the page they asked for.

   Events are delegated from document, so a theme-editor re-render of the
   section needs no re-binding. */
(() => {
  'use strict';

  const GR = window.GR;
  if (!GR || typeof GR.fetchSection !== 'function') {
    console.error('[gr-facets] gr-shop.js has not loaded; filters fall back to plain forms and links.');
    return;
  }
  if (GR.facets) return; // loaded twice (theme editor): the delegated handlers already exist
  GR.facets = true;

  document.documentElement.classList.add('gr-facets-ready');

  const FADE_MS = 200;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const results = () => document.querySelector('[data-gr-results]');
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  let inFlight = null;

  /* ── URL from a form: its fields, minus empty values (a blank price box) ── */
  const urlFromForm = (form) => {
    const params = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      if (String(value).trim() !== '') params.append(key, value);
    });
    const url = new URL(form.getAttribute('action') || window.location.pathname, window.location.origin);
    url.search = params.toString();
    return url.toString();
  };

  /* ── Keep what the visitor was doing across a re-render ─────────────────
     The forms are rebuilt from the server, so focus, collapsed groups, open
     "Show all" lists, find-box text and scroll positions are recorded by
     stable keys (form id + filter param + input name/value) and put back. */
  const SCROLLERS = { sidebar: '[data-gr-results] .collection > aside', drawer: '[data-gr-drawer-part="body"]' };
  const groupKey = (el) => {
    const form = el.closest('form');
    const group = el.closest('[data-gr-param]');
    return form && group ? `${form.id}|${group.dataset.grParam}` : null;
  };

  const captureUi = () => {
    const ui = { collapsed: new Set(), openMore: new Set(), closedMore: new Set(), find: new Map(), scroll: new Map(), focus: null };
    document.querySelectorAll('[data-gr-facets-form] .fgroup__btn[aria-expanded="false"]').forEach((btn) => {
      const key = groupKey(btn);
      if (key) ui.collapsed.add(key);
    });
    document.querySelectorAll('[data-gr-facets-form] [data-gr-more]').forEach((details) => {
      const key = groupKey(details);
      if (key) (details.open ? ui.openMore : ui.closedMore).add(key);
    });
    document.querySelectorAll('[data-gr-facets-form] [data-gr-find]').forEach((input) => {
      const key = groupKey(input);
      if (key && input.value) ui.find.set(key, input.value);
    });
    Object.entries(SCROLLERS).forEach(([key, selector]) => {
      const el = document.querySelector(selector);
      if (el && el.scrollTop) ui.scroll.set(key, el.scrollTop);
    });
    const active = document.activeElement;
    if (active && active !== document.body) {
      const form = active.closest('form');
      if (active.matches('input[type="checkbox"], input[type="number"]') && form) {
        ui.focus = { form: form.id, name: active.name, value: active.type === 'checkbox' ? active.value : null };
      } else if (active.id) {
        ui.focus = { id: active.id };
      }
    }
    return ui;
  };

  const restoreUi = (ui) => {
    document.querySelectorAll('[data-gr-facets-form] .fgroup__btn').forEach((btn) => {
      if (!ui.collapsed.has(groupKey(btn))) return;
      btn.setAttribute('aria-expanded', 'false');
      const body = document.getElementById(btn.getAttribute('aria-controls'));
      if (body) body.hidden = true;
    });
    document.querySelectorAll('[data-gr-facets-form] [data-gr-more]').forEach((details) => {
      const key = groupKey(details);
      if (ui.openMore.has(key)) details.open = true;
      else if (ui.closedMore.has(key)) details.open = false;
    });
    document.querySelectorAll('[data-gr-facets-form] [data-gr-find]').forEach((input) => {
      const value = ui.find.get(groupKey(input));
      if (value) {
        input.value = value;
        applyFind(input);
      }
    });
    ui.scroll.forEach((top, key) => {
      const el = document.querySelector(SCROLLERS[key]);
      if (el) el.scrollTop = top;
    });
    if (!ui.focus) return;
    let target = null;
    if (ui.focus.id) {
      target = document.getElementById(ui.focus.id);
    } else {
      const form = document.getElementById(ui.focus.form);
      if (form) {
        const inputs = Array.from(form.querySelectorAll('input')).filter((i) => i.name === ui.focus.name);
        target = inputs.find((i) => ui.focus.value === null || i.value === ui.focus.value) || null;
      }
    }
    if (target && !target.disabled) target.focus({ preventScroll: true });
  };

  /* ── Re-render the results for a URL ───────────────────────────────────── */
  const setBusy = (root, busy) => {
    const main = root && root.querySelector('[data-gr-results-main]');
    if (!main) return;
    if (busy) main.setAttribute('aria-busy', 'true');
    else main.removeAttribute('aria-busy');
  };

  const swap = (doc) => {
    const next = doc.querySelector('[data-gr-results]');
    const current = results();
    if (!next || !current) throw new Error('The re-rendered section has no [data-gr-results] root.');
    const ui = captureUi();
    current.replaceWith(document.importNode(next, true));
    // The drawer stays in place (it may be open); only its body and footer are replaced.
    ['body', 'foot'].forEach((part) => {
      const selector = `[data-gr-drawer-part="${part}"]`;
      const mine = document.querySelector(selector);
      const theirs = doc.querySelector(selector);
      if (mine && theirs) mine.replaceWith(document.importNode(theirs, true));
    });
    restoreUi(ui);
    GR.layer.wire();
  };

  const navigate = async (url, { push = true, scrollToTop = false } = {}) => {
    const root = results();
    if (!root) {
      window.location.assign(url);
      return;
    }
    if (inFlight) inFlight.abort();
    const controller = new AbortController();
    inFlight = controller;
    setBusy(root, true);
    try {
      const [doc] = await Promise.all([
        GR.fetchSection(url, root.dataset.grSection, { signal: controller.signal }),
        reduceMotion.matches ? null : wait(FADE_MS)
      ]);
      if (controller.signal.aborted) return;
      swap(doc);
      if (push) window.history.pushState({ grResults: true }, '', url);
      const fresh = results();
      if (fresh && (scrollToTop || fresh.getBoundingClientRect().top < 0)) {
        fresh.scrollIntoView({ block: 'start', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('[gr-facets] The results could not be updated in place; loading the page instead.', error);
      window.location.assign(url);
    } finally {
      if (inFlight === controller) {
        inFlight = null;
        setBusy(results(), false);
      }
    }
  };

  /* ── Load more: append the next page's cards, swap the pager ───────────── */
  const loadMore = async (link) => {
    const root = results();
    const grid = root && root.querySelector('[data-gr-grid]');
    const pager = link.closest('[data-gr-pager]');
    if (!grid || !pager || link.getAttribute('aria-disabled') === 'true') {
      if (!grid || !pager) window.location.assign(link.href);
      return;
    }
    const hadFocus = document.activeElement === link;
    link.setAttribute('aria-disabled', 'true');
    try {
      const doc = await GR.fetchSection(link.href, root.dataset.grSection);
      const cards = Array.from(doc.querySelectorAll('[data-gr-grid] > *')).map((card) => document.importNode(card, true));
      const nextPager = doc.querySelector('[data-gr-pager]');
      if (!cards.length) throw new Error('The next page rendered no product cards.');
      grid.append(...cards);
      if (nextPager) {
        const status = pager.querySelector('[role="status"]');
        const imported = document.importNode(nextPager, true);
        const nextStatus = imported.querySelector('[role="status"]');
        // Keep the existing live region node so screen readers announce the new count.
        if (status && nextStatus) {
          status.innerHTML = nextStatus.innerHTML;
          nextStatus.replaceWith(status);
        }
        pager.replaceWith(imported);
      } else {
        pager.remove();
      }
      if (hadFocus) cards[0].querySelector('.pcard__name a')?.focus({ preventScroll: true });
      GR.layer.wire();
    } catch (error) {
      console.error('[gr-facets] Load more failed; loading the next page instead.', error);
      window.location.assign(link.href);
    }
  };

  /* ── Find box: narrow a long filter list ───────────────────────────────── */
  const fold = (text) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const applyFind = (input) => {
    const body = input.closest('.fgroup__body');
    if (!body) return;
    const query = fold(input.value.trim());
    body.querySelectorAll('label.fopt:not([data-gr-find-wrap])').forEach((option) => {
      option.hidden = query !== '' && !fold(option.textContent).includes(query);
    });
    const more = body.querySelector('[data-gr-more]');
    if (more && query) more.open = true;
  };

  /* ── Delegated events ──────────────────────────────────────────────────── */
  document.addEventListener('change', (e) => {
    const form = e.target.closest('[data-gr-facets-form], [data-gr-sort-form]');
    if (!form || e.target.matches('[data-gr-find]')) return;
    navigate(urlFromForm(form));
  });

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-gr-facets-form], [data-gr-sort-form]');
    if (!form) return;
    e.preventDefault();
    navigate(urlFromForm(form));
  });

  document.addEventListener('input', (e) => {
    if (e.target.matches('[data-gr-find]')) applyFind(e.target);
  });

  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const more = e.target.closest('[data-gr-load-more]');
    if (more && results()) {
      e.preventDefault();
      loadMore(more);
      return;
    }
    const link = e.target.closest('a[data-gr-nav]');
    if (!link || !results()) return;
    e.preventDefault();
    navigate(link.href, { scrollToTop: Boolean(link.closest('[data-gr-pager]')) });
  });

  // Enter in a find box would submit the whole form; it only narrows the list.
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && e.target.matches('[data-gr-find]')) e.preventDefault();
  });

  if (results()) window.history.replaceState({ ...(window.history.state || {}), grResults: true }, '');
  window.addEventListener('popstate', (e) => {
    if (e.state && e.state.grResults && results()) navigate(window.location.href, { push: false });
  });
})();
